import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { TRADITION_LAYERS } from "./config";

function Toggle({ checked, count, label, onChange, testId }) {
  return (
    <label className="history-chart__toggle">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        data-testid={testId}
      />
      <span>{label}{count === undefined ? null : <> <span>({count})</span></>}</span>
    </label>
  );
}

function culturalCount(layer, counts) {
  const count = counts[layer.slug];
  const figures = count.figures === 1 ? "figure" : "figures";
  const people = count.figures === 1 ? "person" : "people";
  if (layer.slug === "islamic") return `${count.figures} ${figures} + ${count.other} texts/events`;
  if (layer.slug === "egyptian") return `${count.figures} ${people}`;
  return `${count.total} entries`;
}

// A disclosure, NOT a menu. role="menu" carries an arrow-key contract that
// would then have to be implemented, and a set of checkboxes is not a menu
// anyway: the options are independent and the panel stays open while you
// change several of them.
export default function TimelineControls({ counts, onToggle, state, summary }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonRef = useRef(null);
  const wrapperRef = useRef(null);

  const close = useCallback((returnFocus = false) => {
    setOpen(false);
    if (returnFocus) buttonRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close(true);
      }
    };
    // Pointer, not click: a mousedown outside should dismiss before whatever
    // was clicked takes focus.
    const onPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target)) close();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [open, close]);

  // How many layers are on, so the state is legible with the panel shut. A
  // dropdown that hides which options are active is worse than the row it
  // replaced.
  const activeLayers = [
    state.mainstreamFigures, state.allFigures, state.events, state.periods,
    ...TRADITION_LAYERS.map((layer) => state[layer.slug])
  ].filter(Boolean).length;

  const traditionsOn = TRADITION_LAYERS.filter((layer) => state[layer.slug]);

  const groups = [
    {
      legend: "Occult figures",
      toggles: [
        { key: "mainstreamFigures", label: "Include mainstream occult figures",
          count: counts.mainstreamFigures, testId: "history-toggle-mainstream" },
        { key: "allFigures", label: "Include additional figures",
          count: counts.allFigures, testId: "history-toggle-figures" }
      ]
    },
    {
      legend: "Other traditions",
      toggles: TRADITION_LAYERS.map((layer) => ({
        key: layer.slug, label: layer.label,
        count: culturalCount(layer, counts), testId: `history-toggle-${layer.slug}`
      }))
    },
    {
      legend: "Also show",
      toggles: [
        { key: "events", label: "Historical events", count: counts.events,
          testId: "history-toggle-events" },
        { key: "periods", label: "Colour by period", count: undefined,
          testId: "history-toggle-periods" }
      ]
    }
  ];

  return (
    <div className="history-chart__controls">
      {/* The ref is on the button and its panel, not the whole strip: the
          strip runs the width of the chart, and a click in its empty right
          half or on the summary line under it is a click OFF the box. */}
      <div className="history-layers" ref={wrapperRef}>
        <button
          type="button"
          className={open ? "history-layers__button history-layers__button--open"
                          : "history-layers__button"}
          aria-expanded={open}
          aria-controls={panelId}
          ref={buttonRef}
          onClick={() => setOpen((value) => !value)}
          data-testid="history-layers-toggle"
        >
          <span>What to show</span>
          <span className="history-layers__count" data-testid="history-layers-count">
            {activeLayers} on
          </span>
          <span className="history-layers__chevron" aria-hidden="true">▾</span>
        </button>

        {/* Kept mounted and hidden rather than unmounted: the checkboxes are
            the chart's state, and losing them from the document each time the
            panel shuts makes them unreachable to anything holding a reference. */}
        <div
          className="history-layers__panel"
          id={panelId}
          hidden={!open}
          data-testid="history-layers-panel"
        >
          {groups.map((group) => (
            <fieldset className="history-layers__group" key={group.legend}>
              <legend className="history-layers__legend">{group.legend}</legend>
              {group.toggles.map((toggle) => (
                <Toggle
                  key={toggle.key}
                  checked={Boolean(state[toggle.key])}
                  count={toggle.count}
                  label={toggle.label}
                  onChange={(checked) => onToggle(toggle.key, checked)}
                  testId={toggle.testId}
                />
              ))}
            </fieldset>
          ))}
        </div>
      </div>

      {/* Which of the other traditions are on, with the panel shut. These are
          the ones a reader is most likely to forget they left on, because they
          change what the chart is a chart OF. */}
      {traditionsOn.length > 0 && (
        <p className="history-layers__active" data-testid="history-layers-active">
          Also showing {traditionsOn.map((layer) => layer.legend.replace(/ layer$/, "")).join(", ")}.
        </p>
      )}

      <p className="history-chart__live" aria-live="polite">{summary}</p>
    </div>
  );
}
